import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQueryWithReauth } from 'shared/api/baseQuery';
import { fetchAllPages } from 'shared/api/paginate';

const DATASET_PATHS = {
  risk_taxonomy: 'risk-taxonomy',
  control_library: 'control-library',
  obligations: 'obligations',
  audit_universe: 'audit-universe',
  ethics_framework: 'ethics-framework',
  it_assets: 'it-assets',
  loss_events: 'loss-events',
};

const EFFECTIVE_DATASET_PATHS = {
  risk_taxonomy: 'effective-risk-taxonomy',
  control_library: 'effective-control-library',
  obligations: 'effective-obligations',
  audit_universe: 'effective-audit-universe',
  ethics_framework: 'effective-ethics-framework',
  it_assets: 'effective-it-assets',
  loss_events: 'effective-loss-events',
};

const buildDatasetQuery = ({ datasetType, industry, enabled_for_operation } = {}) => {
  const datasetPath = DATASET_PATHS[datasetType] || datasetType;
  const params = new URLSearchParams();

  if (industry) {
    params.set('industry_applicability', industry);
  }

  if (enabled_for_operation !== undefined && enabled_for_operation !== null) {
    params.set('enabled_for_operation', String(enabled_for_operation));
  }

  return {
    url: `/api/v1/master-data/${datasetPath}/${params.toString() ? `?${params.toString()}` : ''}`,
  };
};

const MY_DATASET_PATHS = {
  risk_taxonomy: 'my-risk-taxonomy',
  control_library: 'my-control-library',
  obligations: 'my-obligations',
  audit_universe: 'my-audit-universe',
  ethics_framework: 'my-ethics-framework',
  it_assets: 'my-it-assets',
  loss_events: 'my-loss-events',
};

// MDT-01: the effective/my template lists used to ask for a single
// page_size=1000 page and keep only `results`, silently dropping rows
// past apps.shared.pagination.StandardPagination's max_page_size=500 cap
// (and dropping `count`/`next` so the FE could not even detect
// truncation). Page through the real DRF pagination envelope instead,
// same precedent as CTL-06 (entities/control/api/controlApi.js).
export const fetchAllEffectiveTemplates = (baseQuery, { datasetType } = {}) => {
  const datasetPath = EFFECTIVE_DATASET_PATHS[datasetType] || `effective-${datasetType}`;
  return fetchAllPages(
    baseQuery,
    `/api/v1/master-data/${datasetPath}/`,
    {},
    { pageSize: 500, maxPages: 20 }
  );
};

export const fetchAllMyTemplates = (baseQuery, { datasetType } = {}) => {
  const datasetPath = MY_DATASET_PATHS[datasetType] || `my-${datasetType}`;
  return fetchAllPages(
    baseQuery,
    `/api/v1/master-data/${datasetPath}/`,
    {},
    { pageSize: 500, maxPages: 20 }
  );
};

// MDT-04: rollbackImport invalidated `{ type: 'MasterDataTemplate', id: 'LIST' }`,
// a tag no query provides (getTemplateLists/getEffectiveTemplateLists/
// getMyTemplateLists provide `LIST_<type>`/`EFFECTIVE_LIST_<type>`/
// `LIST_<type>` respectively) -- so an open dataset list stayed showing the
// just-rolled-back data until something unrelated invalidated it. Mirrors
// the tag set createMyTemplate/deleteMyTemplate already invalidate for the
// same datasetType. Exported via masterDataApiTestUtils because RTK Query
// does not expose a mutation's raw `invalidatesTags` config on the built
// `api.endpoints[name]` object for direct inspection (same constraint
// documented in governanceApi.js's GOV-12 fix).
const rollbackImportInvalidatesTags = (result, error, arg) => {
  const datasetType = arg?.datasetType;
  const tags = [{ type: 'MasterDataTemplate', id: 'IMPORT_HISTORY' }];
  if (datasetType) {
    tags.push(
      { type: 'MasterDataTemplate', id: `EFFECTIVE_LIST_${datasetType}` },
      { type: 'MyMasterData', id: `LIST_${datasetType}` }
    );
  } else {
    // datasetType unknown at the call site -- invalidate every
    // MasterDataTemplate-tagged list rather than silently leaving some
    // dataset's cached rows stale.
    tags.push({ type: 'MasterDataTemplate' });
  }
  return tags;
};

const TAXONOMY_BASE = '/api/v1/master-data/taxonomy';

// MDT-05: the panel needs the Retry-After header on a 429, which the plain
// RTK error object drops.
const keepRetryAfter = (response, meta) => ({
  ...response,
  retryAfter: meta?.response?.headers?.get?.('Retry-After') || null,
});

// MDT-06: node list params. include_inactive only has an effect for
// master_data:manage users (the server ignores it for readers).
const buildTaxonomyNodeParams = ({ module, includeInactive, search } = {}) => {
  const params = {};
  if (module) params.module = module;
  if (includeInactive) params.include_inactive = 'true';
  if (search) params.search = search;
  return params;
};

const taxonomyNodeListTags = (result) => [
  ...(Array.isArray(result) ? result.map(({ id }) => ({ type: 'TaxonomyNode', id })) : []),
  { type: 'TaxonomyNode', id: 'LIST' },
];

const taxonomyNodeChangedTags = (result, error, arg) => [
  { type: 'TaxonomyNode', id: 'LIST' },
  ...(arg?.id ? [{ type: 'TaxonomyNode', id: arg.id }] : []),
];

export const masterDataApi = createApi({
  reducerPath: 'masterDataApi',
  baseQuery: baseQueryWithReauth,
  tagTypes: [
    'MasterDataTemplate',
    'MasterDataOverride',
    'MyMasterData',
    'TaxonomyNode',
    'TaxonomyAnomaly',
    'TaxonomyLevel4',
  ],
  endpoints: (builder) => ({
    getTemplateLists: builder.query({
      query: (params) => buildDatasetQuery(params),
      transformResponse: (response) => response?.results || response || [],
      providesTags: (result, error, arg) => [
        { type: 'MasterDataTemplate', id: `LIST_${arg?.datasetType || 'ALL'}` },
      ],
    }),

    getEffectiveTemplateLists: builder.query({
      async queryFn(arg = {}, _queryApi, _extraOptions, baseQuery) {
        const result = await fetchAllEffectiveTemplates(baseQuery, arg);
        if (result.error) {
          return { error: result.error };
        }
        return { data: result.data };
      },
      providesTags: (result, error, arg) => [
        { type: 'MasterDataTemplate', id: `EFFECTIVE_LIST_${arg?.datasetType || 'ALL'}` },
      ],
    }),

    getTemplateDetail: builder.query({
      query: ({ datasetType, id }) => {
        const datasetPath = DATASET_PATHS[datasetType] || datasetType;
        return {
          url: `/api/v1/master-data/${datasetPath}/${id}/`,
        };
      },
      providesTags: (result, error, arg) => [{ type: 'MasterDataTemplate', id: arg?.id }],
    }),

    importTemplate: builder.mutation({
      query: ({ formData, dryRun = false }) => ({
        url: `/api/v1/master-data/import/${dryRun ? '?dry_run=true' : ''}`,
        method: 'POST',
        body: formData,
      }),
      invalidatesTags: [{ type: 'MasterDataTemplate', id: 'LIST_ALL' }],
    }),

    listOverrides: builder.query({
      query: () => ({
        url: '/api/v1/master-data/overrides/',
      }),
      transformResponse: (response) => response?.results || response || [],
      providesTags: (result) =>
        result
          ? [
              ...result.map(({ id }) => ({ type: 'MasterDataOverride', id })),
              { type: 'MasterDataOverride', id: 'LIST' },
            ]
          : [{ type: 'MasterDataOverride', id: 'LIST' }],
    }),

    createOverride: builder.mutation({
      query: (payload) => ({
        url: '/api/v1/master-data/overrides/',
        method: 'POST',
        body: payload,
      }),
      invalidatesTags: [{ type: 'MasterDataOverride', id: 'LIST' }],
    }),

    updateOverride: builder.mutation({
      query: ({ id, ...patch }) => ({
        url: `/api/v1/master-data/overrides/${id}/`,
        method: 'PATCH',
        body: patch,
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: 'MasterDataOverride', id },
        { type: 'MasterDataOverride', id: 'LIST' },
      ],
    }),

    // -----------------------------------------------------------------------
    // Tenant-scoped endpoints — returns logged-in tenant's own master data
    // -----------------------------------------------------------------------

    getMyTemplateLists: builder.query({
      async queryFn(arg = {}, _queryApi, _extraOptions, baseQuery) {
        const result = await fetchAllMyTemplates(baseQuery, arg);
        if (result.error) {
          return { error: result.error };
        }
        return { data: result.data };
      },
      providesTags: (result, error, arg) => [
        { type: 'MyMasterData', id: `LIST_${arg?.datasetType || 'ALL'}` },
      ],
    }),

    updateMyTemplate: builder.mutation({
      query: ({ datasetType, id, ...patch }) => {
        const datasetPath = MY_DATASET_PATHS[datasetType] || `my-${datasetType}`;
        return {
          url: `/api/v1/master-data/${datasetPath}/${id}/`,
          method: 'PATCH',
          body: patch,
        };
      },
      invalidatesTags: (result, error, { datasetType }) => [
        { type: 'MasterDataTemplate', id: `EFFECTIVE_LIST_${datasetType}` },
        { type: 'MyMasterData', id: `LIST_${datasetType}` },
      ],
    }),

    createMyTemplate: builder.mutation({
      query: ({ datasetType, ...payload }) => {
        const datasetPath = MY_DATASET_PATHS[datasetType] || `my-${datasetType}`;
        return {
          url: `/api/v1/master-data/${datasetPath}/`,
          method: 'POST',
          body: payload,
        };
      },
      invalidatesTags: (result, error, { datasetType }) => [
        { type: 'MasterDataTemplate', id: `EFFECTIVE_LIST_${datasetType}` },
        { type: 'MyMasterData', id: `LIST_${datasetType}` },
      ],
    }),

    deleteMyTemplate: builder.mutation({
      query: ({ datasetType, id }) => {
        const datasetPath = MY_DATASET_PATHS[datasetType] || `my-${datasetType}`;
        return {
          url: `/api/v1/master-data/${datasetPath}/${id}/`,
          method: 'DELETE',
        };
      },
      invalidatesTags: (result, error, { datasetType }) => [
        { type: 'MasterDataTemplate', id: `EFFECTIVE_LIST_${datasetType}` },
        { type: 'MyMasterData', id: `LIST_${datasetType}` },
      ],
    }),

    getImportHistory: builder.query({
      query: ({ datasetType, page = 1, pageSize = 20 } = {}) => {
        const params = new URLSearchParams({ page: String(page), page_size: String(pageSize) });
        if (datasetType) params.set('dataset_type', datasetType);
        return { url: `/api/v1/master-data/import/history/?${params.toString()}` };
      },
      transformResponse: (response) => ({
        data: response?.data || response?.results || response || [],
        total: response?.total || response?.count || 0,
      }),
      providesTags: [{ type: 'MasterDataTemplate', id: 'IMPORT_HISTORY' }],
    }),

    rollbackImport: builder.mutation({
      query: ({ importId }) => ({
        url: `/api/v1/master-data/import/${importId}/rollback/`,
        method: 'POST',
      }),
      invalidatesTags: rollbackImportInvalidatesTags,
    }),

    // -----------------------------------------------------------------------
    // MDT-06: taxonomy nodes, anomaly review queue, level-4 usage
    // -----------------------------------------------------------------------

    listTaxonomyNodes: builder.query({
      async queryFn(arg = {}, _queryApi, _extraOptions, baseQuery) {
        const result = await fetchAllPages(
          baseQuery,
          `${TAXONOMY_BASE}/nodes/`,
          buildTaxonomyNodeParams(arg),
          { pageSize: 500, maxPages: 20 }
        );
        if (result.error) return { error: result.error };
        return { data: result.data };
      },
      providesTags: taxonomyNodeListTags,
    }),

    createTaxonomyNode: builder.mutation({
      query: (payload) => ({ url: `${TAXONOMY_BASE}/nodes/`, method: 'POST', body: payload }),
      invalidatesTags: [{ type: 'TaxonomyNode', id: 'LIST' }],
    }),

    updateTaxonomyNode: builder.mutation({
      query: ({ id, ...patch }) => ({
        url: `${TAXONOMY_BASE}/nodes/${id}/`,
        method: 'PATCH',
        body: patch,
      }),
      invalidatesTags: taxonomyNodeChangedTags,
    }),

    // D17: DELETE deactivates the node; the server refuses (400) while records use it.
    deactivateTaxonomyNode: builder.mutation({
      query: ({ id }) => ({ url: `${TAXONOMY_BASE}/nodes/${id}/`, method: 'DELETE' }),
      invalidatesTags: taxonomyNodeChangedTags,
    }),

    reactivateTaxonomyNode: builder.mutation({
      query: ({ id }) => ({ url: `${TAXONOMY_BASE}/nodes/${id}/reactivate/`, method: 'POST' }),
      invalidatesTags: taxonomyNodeChangedTags,
    }),

    listTaxonomyAnomalies: builder.query({
      async queryFn({ status, module } = {}, _queryApi, _extraOptions, baseQuery) {
        const params = {};
        if (status) params.status = status;
        if (module) params.module = module;
        const result = await fetchAllPages(baseQuery, `${TAXONOMY_BASE}/anomalies/`, params, {
          pageSize: 200,
          maxPages: 10,
        });
        if (result.error) return { error: result.error };
        return { data: result.data };
      },
      providesTags: [{ type: 'TaxonomyAnomaly', id: 'LIST' }],
    }),

    // action: 'start-review' | 'resolve' | 'dismiss'
    reviewTaxonomyAnomaly: builder.mutation({
      query: ({ id, action, note }) => ({
        url: `${TAXONOMY_BASE}/anomalies/${id}/${action}/`,
        method: 'POST',
        body: note ? { note } : {},
      }),
      invalidatesTags: [{ type: 'TaxonomyAnomaly', id: 'LIST' }],
    }),

    getLevel4Usage: builder.query({
      query: ({ module } = {}) => ({
        url: `${TAXONOMY_BASE}/level4-usage/`,
        params: module ? { module } : undefined,
      }),
      transformResponse: (response) => response?.results || response || [],
      providesTags: [{ type: 'TaxonomyLevel4', id: 'LIST' }],
    }),

    // -----------------------------------------------------------------------
    // MDT-05: AI classification (tenant opt-in, D13)
    // -----------------------------------------------------------------------

    aiClassify: builder.mutation({
      query: ({ module, records }) => ({
        url: `${TAXONOMY_BASE}/ai-classify/`,
        method: 'POST',
        body: { module, records },
      }),
      transformErrorResponse: keepRetryAfter,
    }),

    submitAiClassifyFeedback: builder.mutation({
      query: ({ logId, outcomes }) => ({
        url: `${TAXONOMY_BASE}/ai-classify/${logId}/feedback/`,
        method: 'POST',
        body: { outcomes },
      }),
    }),
  }),
});

export const {
  useGetTemplateListsQuery,
  useGetEffectiveTemplateListsQuery,
  useGetTemplateDetailQuery,
  useImportTemplateMutation,
  useListOverridesQuery,
  useCreateOverrideMutation,
  useUpdateOverrideMutation,
  useGetMyTemplateListsQuery,
  useUpdateMyTemplateMutation,
  useCreateMyTemplateMutation,
  useDeleteMyTemplateMutation,
  useGetImportHistoryQuery,
  useRollbackImportMutation,
  useListTaxonomyNodesQuery,
  useCreateTaxonomyNodeMutation,
  useUpdateTaxonomyNodeMutation,
  useDeactivateTaxonomyNodeMutation,
  useReactivateTaxonomyNodeMutation,
  useListTaxonomyAnomaliesQuery,
  useReviewTaxonomyAnomalyMutation,
  useGetLevel4UsageQuery,
  useAiClassifyMutation,
  useSubmitAiClassifyFeedbackMutation,
} = masterDataApi;

export const masterDataApiTestUtils = {
  rollbackImportInvalidatesTags,
};
